"use client";

import { FocusableContext } from "./SWN3JYXT.js";
import { createElement, createHook, forwardRef } from "./VOQWLFSQ.js";
import { useEvent, useMergeRefs, useTagName } from "./5GGHRIN3.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/focusable/focusable.tsx
import { isButton } from "@ariakit/core/utils/dom";
import { addGlobalEventListener, isFocusEventOutside, isPortalEvent, isSelfTarget, queueBeforeEvent } from "@ariakit/core/utils/events";
import { focusIfNeeded, getClosestFocusable, hasFocus, isFocusable } from "@ariakit/core/utils/focus";
import { disabledFromProps, removeUndefinedValues } from "@ariakit/core/utils/misc";
import { isSafari } from "@ariakit/core/utils/platform";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
var TagName = "div";
var isSafariBrowser = isSafari();
var alwaysFocusVisibleInputTypes = ["text", "search", "url", "tel", "email", "password", "number", "date", "month", "week", "time", "datetime", "datetime-local"];
var safariFocusAncestorSymbol = Symbol("safariFocusAncestor");
function isSafariFocusAncestor(element) {
  if (!element) return false;
  return !!element[safariFocusAncestorSymbol];
}
function markSafariFocusAncestor(element, value) {
  if (!element) return;
  element[safariFocusAncestorSymbol] = value;
}
function isAlwaysFocusVisible(element) {
  const {
    tagName,
    readOnly,
    type
  } = element;
  if (tagName === "TEXTAREA" && !readOnly) return true;
  if (tagName === "SELECT" && !readOnly) return true;
  if (tagName === "INPUT" && !readOnly) {
    return alwaysFocusVisibleInputTypes.includes(type);
  }
  if (element.isContentEditable) return true;
  const role = element.getAttribute("role");
  if (role === "combobox" && element.dataset.name) {
    return true;
  }
  return false;
}
function getLabels(element) {
  if ("labels" in element) {
    return element.labels;
  }
  return null;
}
function isNativeCheckboxOrRadio(element) {
  const tagName = element.tagName.toLowerCase();
  if (tagName === "input" && element.type) {
    return element.type === "radio" || element.type === "checkbox";
  }
  return false;
}
function isNativeTabbable(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "summary" || tagName === "input" || tagName === "select" || tagName === "textarea" || tagName === "a";
}
function supportsDisabledAttribute(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "input" || tagName === "select" || tagName === "textarea";
}
function getTabIndex(focusable, trulyDisabled, nativeTabbable, supportsDisabled, tabIndexProp) {
  if (!focusable) {
    return tabIndexProp;
  }
  if (trulyDisabled) {
    if (nativeTabbable && !supportsDisabled) {
      return -1;
    }
    return;
  }
  if (nativeTabbable) {
    return tabIndexProp;
  }
  return tabIndexProp || 0;
}
function useDisableEvent(onEvent, disabled) {
  return useEvent(event => {
    onEvent == null ? void 0 : onEvent(event);
    if (event.defaultPrevented) return;
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  });
}
var hasInstalledGlobalEventListeners = false;
var isKeyboardModality = true;
function onGlobalMouseDown(event) {
  const target = event.target;
  if (target && "hasAttribute" in target) {
    if (!target.hasAttribute("data-focus-visible")) {
      isKeyboardModality = false;
    }
  }
}
function onGlobalKeyDown(event) {
  if (event.metaKey) return;
  if (event.ctrlKey) return;
  if (event.altKey) return;
  isKeyboardModality = true;
}
var useFocusable = createHook(function useFocusable2(_a) {
  var _b = _a,
    {
      focusable = true,
      accessibleWhenDisabled,
      autoFocus,
      onFocusVisible
    } = _b,
    props = __objRest(_b, ["focusable", "accessibleWhenDisabled", "autoFocus", "onFocusVisible"]);
  const ref = useRef(null);
  useEffect(() => {
    if (!focusable) return;
    if (hasInstalledGlobalEventListeners) return;
    addGlobalEventListener("mousedown", onGlobalMouseDown, true);
    addGlobalEventListener("keydown", onGlobalKeyDown, true);
    hasInstalledGlobalEventListeners = true;
  }, [focusable]);
  if (isSafariBrowser) {
    useEffect(() => {
      if (!focusable) return;
      const element = ref.current;
      if (!element) return;
      if (!isNativeCheckboxOrRadio(element)) return;
      const labels = getLabels(element);
      if (!labels) return;
      const onMouseUp = () => queueMicrotask(() => element.focus());
      for (const label of labels) {
        label.addEventListener("mouseup", onMouseUp);
      }
      return () => {
        for (const label of labels) {
          label.removeEventListener("mouseup", onMouseUp);
        }
      };
    }, [focusable]);
  }
  const disabled = focusable && disabledFromProps(props);
  const trulyDisabled = !!disabled && !accessibleWhenDisabled;
  const [focusVisible, setFocusVisible] = useState(false);
  useEffect(() => {
    if (!focusable) return;
    if (trulyDisabled && focusVisible) {
      setFocusVisible(false);
    }
  }, [focusable, trulyDisabled, focusVisible]);
  useEffect(() => {
    if (!focusable) return;
    if (!focusVisible) return;
    const element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(() => {
      if (!isFocusable(element)) {
        setFocusVisible(false);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [focusable, focusVisible]);
  const onKeyPressCapture = useDisableEvent(props.onKeyPressCapture, disabled);
  const onMouseDownCapture = useDisableEvent(props.onMouseDownCapture, disabled);
  const onClickCapture = useDisableEvent(props.onClickCapture, disabled);
  const onMouseDownProp = props.onMouseDown;
  const onMouseDown = useEvent(event => {
    onMouseDownProp == null ? void 0 : onMouseDownProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    const element = event.currentTarget;
    if (!isSafariBrowser) return;
    if (isPortalEvent(event)) return;
    if (!isButton(element) && !isNativeCheckboxOrRadio(element)) return;
    let receivedFocus = false;
    const onFocus = () => {
      receivedFocus = true;
    };
    const options = {
      capture: true,
      once: true
    };
    element.addEventListener("focusin", onFocus, options);
    const focusableContainer = getClosestFocusable(element.parentElement);
    markSafariFocusAncestor(focusableContainer, true);
    queueBeforeEvent(element, "mouseup", () => {
      element.removeEventListener("focusin", onFocus, true);
      markSafariFocusAncestor(focusableContainer, false);
      if (receivedFocus) return;
      focusIfNeeded(element);
    });
  });
  const handleFocusVisible = (event, currentTarget) => {
    if (currentTarget) {
      event.currentTarget = currentTarget;
    }
    if (!focusable) return;
    const element = event.currentTarget;
    if (!element) return;
    if (!hasFocus(element)) return;
    onFocusVisible == null ? void 0 : onFocusVisible(event);
    if (event.defaultPrevented) return;
    element.dataset.focusVisible = "true";
    setFocusVisible(true);
  };
  const onKeyDownCaptureProp = props.onKeyDownCapture;
  const onKeyDownCapture = useEvent(event => {
    onKeyDownCaptureProp == null ? void 0 : onKeyDownCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    if (focusVisible) return;
    if (event.metaKey) return;
    if (event.altKey) return;
    if (event.ctrlKey) return;
    if (!isSelfTarget(event)) return;
    const element = event.currentTarget;
    const applyFocusVisible = () => handleFocusVisible(event, element);
    queueBeforeEvent(element, "focusout", applyFocusVisible);
  });
  const onFocusCaptureProp = props.onFocusCapture;
  const onFocusCapture = useEvent(event => {
    onFocusCaptureProp == null ? void 0 : onFocusCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    if (!isSelfTarget(event)) {
      setFocusVisible(false);
      return;
    }
    const element = event.currentTarget;
    const applyFocusVisible = () => handleFocusVisible(event, element);
    if (isKeyboardModality || isAlwaysFocusVisible(event.target)) {
      queueBeforeEvent(event.target, "focusout", applyFocusVisible);
    } else {
      setFocusVisible(false);
    }
  });
  const onBlurProp = props.onBlur;
  const onBlur = useEvent(event => {
    onBlurProp == null ? void 0 : onBlurProp(event);
    if (!focusable) return;
    if (!isFocusEventOutside(event)) return;
    event.currentTarget.removeAttribute("data-focus-visible");
    setFocusVisible(false);
  });
  const autoFocusOnShow = useContext(FocusableContext);
  const autoFocusRef = useEvent(element => {
    if (!focusable) return;
    if (!autoFocus) return;
    if (!element) return;
    if (!autoFocusOnShow) return;
    queueMicrotask(() => {
      if (hasFocus(element)) return;
      if (!isFocusable(element)) return;
      element.focus();
    });
  });
  const tagName = useTagName(ref);
  const nativeTabbable = focusable && isNativeTabbable(tagName);
  const supportsDisabled = focusable && supportsDisabledAttribute(tagName);
  const styleProp = props.style;
  const style = useMemo(() => {
    if (trulyDisabled) {
      return __spreadValues({
        pointerEvents: "none"
      }, styleProp);
    }
    return styleProp;
  }, [trulyDisabled, styleProp]);
  props = __spreadProps(__spreadValues({
    "data-focus-visible": focusable && focusVisible || void 0,
    "data-autofocus": autoFocus || void 0,
    "aria-disabled": disabled || void 0
  }, props), {
    ref: useMergeRefs(ref, autoFocusRef, props.ref),
    style,
    tabIndex: getTabIndex(focusable, trulyDisabled, nativeTabbable, supportsDisabled, props.tabIndex),
    disabled: supportsDisabled && trulyDisabled ? true : void 0,
    // TODO: Test Focusable contentEditable.
    contentEditable: disabled ? void 0 : props.contentEditable,
    onKeyPressCapture,
    onClickCapture,
    onMouseDownCapture,
    onMouseDown,
    onKeyDownCapture,
    onFocusCapture,
    onBlur
  });
  return removeUndefinedValues(props);
});
var Focusable = forwardRef(function Focusable2(props) {
  const htmlProps = useFocusable(props);
  return createElement(TagName, htmlProps);
});
export { isSafariFocusAncestor, useFocusable, Focusable };